import { PropsWithChildren, useEffect, useMemo } from 'react'
import gsap from 'gsap'
import { useRouter } from 'next/router'

import LayoutController from '@/components/layout/controller'
import Footer from '@/components/layout/footer'
import Prefooter from '@/components/layout/prefooter'
import { LenisProvider } from '@/contexts/lenis'
import { useLoaderStore } from '@/store/loader'
import { useLocaleStore } from '@/store/locale'
import { useRouterStore } from '@/store/router'
import { pageHasHero } from '@/utils/page'

import { WPPageData } from '@/types'

export type LayoutProps = PropsWithChildren<{ pageProps: WPPageData }>

const Layout = ({ children, pageProps }: LayoutProps) => {
  const router = useRouter()
  const setCurrentLocale = useLocaleStore((state) => state.setCurrentLocale)
  const setCurrentRoute = useRouterStore((state) => state.setCurrentRoute)
  const setCurrentTranslations = useLocaleStore(
    (store) => store.setCurrentTranslations
  )
  const setLoaderState = useLoaderStore((state) => state.setLoaderState)
  const hasHero = useMemo(() => pageHasHero(pageProps), [pageProps])

  useEffect(() => {
    setCurrentRoute(router.asPath)
    setCurrentLocale(router.locale)
    setCurrentTranslations(pageProps.wpml_translations || [])
  }, [
    router,
    pageProps.wpml_translations,
    setCurrentRoute,
    setCurrentLocale,
    setCurrentTranslations
  ])

  useEffect(() => {
    const background = pageProps.acf?.page?.background
    if (!background) return

    document.documentElement.classList.toggle('dark', background === 'dark')

    const colorMap: Record<string, string> = {
      light: '#eee',
      dark: '#0d0d0d',
      custom: pageProps.acf?.page?.background_color || '#fff'
    }
    const bg = colorMap[background] ?? '#fff'

    document.documentElement.style.setProperty('--page-background', bg)
  }, [pageProps.acf?.page?.background, pageProps.acf?.page?.background_color])

  useEffect(() => {
    if (hasHero) return
    const delayed = gsap.delayedCall(0.3, () => {
      setLoaderState({ heroLoaded: true })
    })
    return () => {
      delayed.kill()
    }
  }, [hasHero, setLoaderState])

  return (
    <LenisProvider>
      <LayoutController pageProps={pageProps} />
      {children}
      {pageProps.acf?.footer?.footer ? (
        <>
          <Prefooter
            dark={pageProps.acf?.page?.background === 'dark'}
            footer={pageProps.acf.footer}
          />
          <Footer footer={pageProps.acf.footer} />
        </>
      ) : null}
    </LenisProvider>
  )
}

export default Layout
